// Wave 3.45 Phase 2 — Broken Packet primitive
// =============================================================================
// BrokenPacketCreateDialog
// =============================================================================
// Shared dialog used by Diamonds / Color Stones / Pearls list pages to create
// a new Broken (salvage) Packet. The dialog is intentionally minimal — broken
// packets are vendorless empty containers; the only required input is the
// classification + an optional description.
//
// Backend endpoints by ``material``:
//   diamond     →  POST /api/diamonds/packets/broken
//   color_stone →  POST /api/color-stones/packets/broken
//   pearl       →  POST /api/pearls/packets/broken
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { AlertTriangle, Loader2, PackageX } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';

const MATERIAL_CONFIG = {
  diamond: {
    label: 'Diamond',
    apiPrefix: '/diamonds',
    classificationsPath: '/diamonds/classifications',
  },
  color_stone: {
    label: 'Colour Stone',
    apiPrefix: '/color-stones',
    classificationsPath: '/color-stones/classifications',
  },
  pearl: {
    label: 'Pearl',
    apiPrefix: '/pearls',
    classificationsPath: '/pearls/classifications',
  },
};

export function BrokenPacketCreateDialog({
  open,
  onOpenChange,
  material,
  companyId,
  onCreated,
  // When the host page is filtered by a classification, pre-select it.
  defaultClassificationId = '',
}) {
  const cfg = MATERIAL_CONFIG[material];
  const [classifications, setClassifications] = useState([]);
  const [classificationId, setClassificationId] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !cfg) return;
    setClassificationId(defaultClassificationId || '');
    setDescription('');
    loadClassifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, material]);

  const loadClassifications = async () => {
    if (!cfg) return;
    setLoading(true);
    try {
      const { data } = await api.get(cfg.classificationsPath, {
        params: { company_id: companyId },
      });
      setClassifications(data || []);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load classifications'));
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!classificationId) {
      toast.error('Please select a classification');
      return;
    }
    if (!cfg) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`${cfg.apiPrefix}/packets/broken`, {
        company_id: companyId,
        classification_id: classificationId,
        description: description.trim(),
      });
      toast.success(`Broken packet ${data.packet_no} created`);
      onCreated?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to create broken packet'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!cfg) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        data-testid={`broken-packet-dialog-${material}`}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <PackageX size={18} className="text-rose-600" />
            <DialogTitle>New Broken {cfg.label} Packet</DialogTitle>
          </div>
          <DialogDescription>
            Create an empty <span className="font-medium text-rose-700">salvage container</span> for
            damaged stones returned by goldsmiths. Broken packets cannot be issued, transferred
            or sold — they are visible only via the &quot;Broken&quot; filter.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-[12px] text-rose-800 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Broken packets are <strong>vendorless</strong> and start with{' '}
            <strong>zero stock</strong>. Stones can later be routed into them when goldsmiths
            return damaged material.
          </span>
        </div>

        <div className="space-y-3 mt-3">
          <div>
            <Label className="text-[11px] text-slate-500">
              Classification <span className="text-rose-600">*</span>
            </Label>
            <SearchableSelect
              items={classifications || []}
              value={classificationId}
              onValueChange={(v) => setClassificationId(v || '')}
              itemToLabel={(c) => `${c.schema_code} · ${c.shape_name}`}
              searchKeys={['schema_code', 'shape_name']}
              placeholder={loading ? 'Loading…' : 'Select classification'}
              searchPlaceholder="Search shape / code…"
              loading={loading}
              disabled={loading || (classifications || []).length === 0}
              testId="broken-packet-classification-select"
            />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Damaged stones returned from rework — 2026 batch"
              rows={2}
              data-testid="broken-packet-description-input"
            />
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            data-testid="broken-packet-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || loading || !classificationId}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="broken-packet-submit-button"
          >
            {submitting ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <PackageX size={14} className="mr-1.5" />
            )}
            {submitting ? 'Creating…' : 'Create Broken Packet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BrokenPacketCreateDialog;
